import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import axios from './axiosConfig';

function D3Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    institucion: 'all',
    area: 'all'
  });
  
  // Referencias para los contenedores de gráficos
  const pubAnioRef = useRef();
  const topInvRef = useRef();
  const instRef = useRef();
  const deptRef = useRef();
  const areasRef = useRef();
  const tendenciaRef = useRef();
  const tiposRef = useRef();
  const idiomasRef = useRef();
  const oaRef = useRef();
  const generoRef = useRef();

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.institucion !== 'all') params.append('filtro_institucion', filters.institucion);
      if (filters.area !== 'all') params.append('filtro_area', filters.area);
      
      const response = await axios.get(`/api/dashboard/data?${params.toString()}`);
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data && !loading) {
      createVisualizaciones();
    }
  }, [data, loading]);

  const createVisualizaciones = () => {
    try {
      createPublicacionesPorAnio();
      createTopInvestigadores();
      createDistribucionInstituciones();
      createDistribucionDepartamentos();
      createAreasConocimiento();
      createTendenciaCitas();
      createTiposPublicacion();
      createIdiomasChart();
      createOpenAccessChart();
      createGeneroChart();
    } catch (e) {
      console.error('D3Dashboard visualizaciones:', e);
    }
  };

  const createPublicacionesPorAnio = () => {
    if (!data.publicaciones_por_anio || data.publicaciones_por_anio.length === 0) return;

    const container = d3.select(pubAnioRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 50 };
    const width = 500 - margin.left - margin.right;
    const height = 300 - margin.bottom - margin.top;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Escalas
    const xScale = d3.scaleBand()
      .domain(data.publicaciones_por_anio.map(d => d.anio))
      .range([0, width])
      .padding(0.2);

    const maxCant = d3.max(data.publicaciones_por_anio, d => d.cantidad) ?? 0;
    const yScale = d3.scaleLinear()
      .domain([0, Math.max(maxCant, 1)])
      .range([height, 0]);

    // Gradiente
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "bar-gradient")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0).attr("y1", height)
      .attr("x2", 0).attr("y2", 0);
    
    gradient.append("stop").attr("offset", "0%").attr("stop-color", "#3B82F6");
    gradient.append("stop").attr("offset", "100%").attr("stop-color", "#6366F1");

    // Barras
    g.selectAll(".bar")
      .data(data.publicaciones_por_anio)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("x", d => xScale(d.anio))
      .attr("width", xScale.bandwidth())
      .attr("y", height)
      .attr("height", 0)
      .attr("fill", "url(#bar-gradient)")
      .attr("rx", 4)
      .on("mouseover", function(event, d) {
        d3.select(this).style("opacity", 0.8);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`Año: ${d.anio}<br/>Publicaciones: ${d.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).style("opacity", 1);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .attr("y", d => yScale(d.cantidad))
      .attr("height", d => height - yScale(d.cantidad));

    // Ejes
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");
  };

  const createTopInvestigadores = () => {
    if (!data.top_investigadores || data.top_investigadores.length === 0) return;

    const container = d3.select(topInvRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 150 };
    const width = 400 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Escalas
    const yScale = d3.scaleBand()
      .domain(data.top_investigadores.map(d => d.nombre))
      .range([0, height])
      .padding(0.2);

    const maxPub = d3.max(data.top_investigadores, d => d.publicaciones) ?? 0;
    const xScale = d3.scaleLinear()
      .domain([0, Math.max(maxPub, 1)])
      .range([0, width]);

    // Color scale
    const colorScale = d3.scaleSequential(d3.interpolateViridis)
      .domain([0, Math.max(maxPub, 1)]);

    // Barras horizontales
    g.selectAll(".bar")
      .data(data.top_investigadores)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("y", d => yScale(d.nombre))
      .attr("height", yScale.bandwidth())
      .attr("x", 0)
      .attr("width", 0)
      .attr("fill", d => colorScale(d.publicaciones))
      .attr("rx", 4)
      .on("mouseover", function(event, d) {
        d3.select(this).style("opacity", 0.8);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.nombre}<br/>Publicaciones: ${d.publicaciones}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).style("opacity", 1);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .attr("width", d => xScale(d.publicaciones));

    // Ejes
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "11px")
      .text(d => d.length > 20 ? d.substring(0, 20) + "..." : d);
  };

  const createDistribucionInstituciones = () => {
    if (!data.distribucion_instituciones || data.distribucion_instituciones.length === 0) return;

    const container = d3.select(instRef.current);
    container.selectAll("*").remove();

    const width = 350;
    const height = 350;
    const radius = Math.min(width, height) / 2 - 20;

    const svg = container
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(data.distribucion_instituciones.map(d => d.institucion))
      .range(["#3B82F6", "#8B5CF6", "#06B6D4", "#10B981", "#F59E0B", "#EF4444", "#EC4899", "#6366F1"]);

    // Pie generator
    const pie = d3.pie()
      .value(d => d.cantidad)
      .sort(null);

    // Arc generator
    const arc = d3.arc()
      .innerRadius(radius * 0.4)
      .outerRadius(radius);

    const arcHover = d3.arc()
      .innerRadius(radius * 0.4)
      .outerRadius(radius + 5);

    // Crear arcos
    const arcs = g.selectAll(".arc")
      .data(pie(data.distribucion_instituciones))
      .enter().append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.institucion))
      .style("opacity", 0)
      .on("mouseover", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arcHover);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.data.institucion}<br/>Investigadores: ${d.data.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .style("opacity", 1)
      .attrTween("d", function(d) {
        const interpolate = d3.interpolate({startAngle: 0, endAngle: 0}, d);
        return function(t) {
          return arc(interpolate(t));
        };
      });
  };

  const createDistribucionDepartamentos = () => {
    if (!data.departamentos || data.departamentos.length === 0) return;

    const container = d3.select(deptRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 160 };
    const width = 420 - margin.left - margin.right;
    const height = 320 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const yScale = d3.scaleBand()
      .domain(data.departamentos.map(d => d.departamento))
      .range([0, height])
      .padding(0.2);

    const maxDept = d3.max(data.departamentos, d => d.cantidad) ?? 0;
    const xScale = d3.scaleLinear()
      .domain([0, Math.max(maxDept, 1)])
      .range([0, width]);

    const colorScale = d3.scaleSequential(d3.interpolateBlues)
      .domain([0, Math.max(maxDept, 1)]);

    g.selectAll(".bar")
      .data(data.departamentos)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("y", d => yScale(d.departamento))
      .attr("height", yScale.bandwidth())
      .attr("x", 0)
      .attr("width", 0)
      .attr("fill", d => colorScale(d.cantidad))
      .attr("rx", 4)
      .on("mouseover", function (event, d) {
        d3.select(this).style("opacity", 0.8);
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.departamento}<br/>Investigadores: ${d.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function () {
        d3.select(this).style("opacity", 1);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 80)
      .attr("width", d => xScale(d.cantidad));

    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(5))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "11px")
      .text(d => d.length > 25 ? d.substring(0, 25) + "..." : d);
  };

  const createAreasConocimiento = () => {
    if (!data.areas_conocimiento || data.areas_conocimiento.length === 0) return;

    const container = d3.select(areasRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 120 };
    const width = 400 - margin.left - margin.right;
    const height = 300 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Escalas
    const yScale = d3.scaleBand()
      .domain(data.areas_conocimiento.map(d => d.area))
      .range([0, height])
      .padding(0.2);

    const maxArea = d3.max(data.areas_conocimiento, d => d.cantidad) ?? 0;
    const xScale = d3.scaleLinear()
      .domain([0, Math.max(maxArea, 1)])
      .range([0, width]);

    // Color scale
    const colorScale = d3.scaleSequential(d3.interpolatePlasma)
      .domain([0, Math.max(maxArea, 1)]);

    // Barras
    g.selectAll(".bar")
      .data(data.areas_conocimiento)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("y", d => yScale(d.area))
      .attr("height", yScale.bandwidth())
      .attr("x", 0)
      .attr("width", 0)
      .attr("fill", d => colorScale(d.cantidad))
      .attr("rx", 4)
      .on("mouseover", function(event, d) {
        d3.select(this).style("opacity", 0.8);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.area}<br/>Investigadores: ${d.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).style("opacity", 1);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .attr("width", d => xScale(d.cantidad));

    // Ejes
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "11px")
      .text(d => d.length > 15 ? d.substring(0, 15) + "..." : d);
  };

  const createTendenciaCitasLegacy = () => {
    if (!data.tendencia_citas || data.tendencia_citas.length === 0) return;

    const container = d3.select(tendenciaRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const width = 800 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Escalas
    const xScale = d3.scaleLinear()
      .domain(d3.extent(data.tendencia_citas, d => d.anio))
      .range([0, width]);

    const maxCitas = d3.max(data.tendencia_citas, d => d.citas_totales) ?? 0;
    const yScale = d3.scaleLinear()
      .domain([0, Math.max(maxCitas, 1)])
      .range([height, 0]);

    // Gradiente para el área
    const gradient = svg.append("defs")
      .append("linearGradient")
      .attr("id", "area-gradient-citas")
      .attr("gradientUnits", "userSpaceOnUse")
      .attr("x1", 0).attr("y1", height)
      .attr("x2", 0).attr("y2", 0);
    
    gradient.append("stop").attr("offset", "0%").attr("stop-color", "#10B981").attr("stop-opacity", 0.1);
    gradient.append("stop").attr("offset", "100%").attr("stop-color", "#10B981").attr("stop-opacity", 0.3);

    // Línea
    const line = d3.line()
      .x(d => xScale(d.anio))
      .y(d => yScale(d.citas_totales))
      .curve(d3.curveMonotoneX);

    // Área
    const area = d3.area()
      .x(d => xScale(d.anio))
      .y0(height)
      .y1(d => yScale(d.citas_totales))
      .curve(d3.curveMonotoneX);

    // Crear área
    g.append("path")
      .datum(data.tendencia_citas)
      .attr("fill", "url(#area-gradient-citas)")
      .attr("d", area)
      .style("opacity", 0)
      .transition()
      .duration(1500)
      .style("opacity", 1);

    // Crear línea
    const path = g.append("path")
      .datum(data.tendencia_citas)
      .attr("fill", "none")
      .attr("stroke", "#10B981")
      .attr("stroke-width", 3)
      .attr("d", line);

    // Animar línea
    const pathNode = path.node();
    const totalLength =
      pathNode && typeof pathNode.getTotalLength === 'function'
        ? pathNode.getTotalLength()
        : 0;
    path
      .attr("stroke-dasharray", totalLength + " " + totalLength)
      .attr("stroke-dashoffset", totalLength)
      .transition()
      .duration(2000)
      .ease(d3.easeLinear)
      .attr("stroke-dashoffset", 0);

    // Puntos
    g.selectAll(".dot")
      .data(data.tendencia_citas)
      .enter().append("circle")
      .attr("class", "dot")
      .attr("cx", d => xScale(d.anio))
      .attr("cy", d => yScale(d.citas_totales))
      .attr("r", 0)
      .attr("fill", "#10B981")
      .on("mouseover", function(event, d) {
        d3.select(this).transition().duration(150).attr("r", 6);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`Año: ${d.anio}<br/>Citas totales: ${d.citas_totales}<br/>Promedio: ${d.citas_promedio}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).transition().duration(150).attr("r", 4);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 200 + 1000)
      .attr("r", 4);

    // Ejes
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).tickFormat(d3.format("d")))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");
  };

  const createTendenciaCitas = () => {
    if (!data.tendencia_citas || data.tendencia_citas.length === 0) return;

    const container = d3.select(tendenciaRef.current);
    container.selectAll("*").remove();

    const multiInstitutionSeries =
      filters.institucion === 'all' &&
      Array.isArray(data.tendencia_citas_instituciones) &&
      data.tendencia_citas_instituciones.length > 0;

    const allPoints = multiInstitutionSeries
      ? data.tendencia_citas_instituciones.flatMap((serie) =>
          (serie.serie || []).map((point) => ({
            ...point,
            institucion: serie.institucion,
          }))
        )
      : data.tendencia_citas;

    if (!allPoints.length) return;

    const margin = { top: 20, right: 30, bottom: 40, left: 60 };
    const width = 800 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    if (multiInstitutionSeries) {
      const institutionPalette = [
        "#10B981",
        "#3B82F6",
        "#F59E0B",
        "#F43F5E",
        "#8B5CF6",
        "#06B6D4",
        "#84CC16",
        "#F97316",
        "#14B8A6",
        "#6366F1",
      ];

      const legend = container
        .append("div")
        .style("display", "flex")
        .style("flex-direction", "column")
        .style("align-items", "flex-start")
        .style("gap", "8px")
        .style("margin-bottom", "12px")
        .style("font-size", "12px")
        .style("width", "fit-content")
        .style("margin-left", "auto")
        .style("margin-right", "auto");

      const colorScale = d3.scaleOrdinal()
        .domain(data.tendencia_citas_instituciones.map((serie) => serie.institucion))
        .range(institutionPalette);

      data.tendencia_citas_instituciones.forEach((serie) => {
        const item = legend.append("div")
          .style("display", "flex")
          .style("align-items", "center")
          .style("gap", "6px");

        item.append("span")
          .style("width", "10px")
          .style("height", "10px")
          .style("border-radius", "9999px")
          .style("background-color", colorScale(serie.institucion));

        item.append("span")
          .style("color", "#475569")
          .style("font-weight", "600")
          .text(serie.institucion);
      });
    }

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    const xScale = d3.scaleLinear()
      .domain(d3.extent(allPoints, d => d.anio))
      .range([0, width]);

    const maxCitas = d3.max(allPoints, d => d.citas_totales) ?? 0;
    const yScale = d3.scaleLinear()
      .domain([0, Math.max(maxCitas, 1)])
      .range([height, 0]);

    if (multiInstitutionSeries) {
      const institutionPalette = [
        "#10B981",
        "#3B82F6",
        "#F59E0B",
        "#F43F5E",
        "#8B5CF6",
        "#06B6D4",
        "#84CC16",
        "#F97316",
        "#14B8A6",
        "#6366F1",
      ];

      const colorScale = d3.scaleOrdinal()
        .domain(data.tendencia_citas_instituciones.map((serie) => serie.institucion))
        .range(institutionPalette);

      const line = d3.line()
        .x(d => xScale(d.anio))
        .y(d => yScale(d.citas_totales))
        .curve(d3.curveMonotoneX);

      const area = d3.area()
        .x(d => xScale(d.anio))
        .y0(height)
        .y1(d => yScale(d.citas_totales))
        .curve(d3.curveMonotoneX);

      data.tendencia_citas_instituciones.forEach((serie, index) => {
        const serieData = (serie.serie || []).map((point) => ({
          ...point,
          institucion: serie.institucion,
        }));
        const color = colorScale(serie.institucion);

        g.append("path")
          .datum(serieData)
          .attr("fill", color)
          .attr("fill-opacity", 0.12)
          .attr("d", area)
          .style("opacity", 0)
          .transition()
          .duration(900)
          .delay(index * 120)
          .style("opacity", 1);

        const path = g.append("path")
          .datum(serieData)
          .attr("fill", "none")
          .attr("stroke", color)
          .attr("stroke-width", 3)
          .attr("opacity", 0.95)
          .attr("stroke-linecap", "round")
          .attr("stroke-linejoin", "round")
          .attr("d", line);

        const pathNode = path.node();
        const totalLength =
          pathNode && typeof pathNode.getTotalLength === 'function'
            ? pathNode.getTotalLength()
            : 0;

        path
          .attr("stroke-dasharray", totalLength + " " + totalLength)
          .attr("stroke-dashoffset", totalLength)
          .transition()
          .duration(1200)
          .delay(index * 120)
          .ease(d3.easeLinear)
          .attr("stroke-dashoffset", 0);

        g.selectAll(`.dot-${index}`)
          .data(serieData)
          .enter().append("circle")
          .attr("class", `dot-${index}`)
          .attr("cx", d => xScale(d.anio))
          .attr("cy", d => yScale(d.citas_totales))
          .attr("r", 0)
          .attr("fill", color)
          .attr("stroke", "#ffffff")
          .attr("stroke-width", 1.5)
          .on("mouseover", function(event, d) {
            d3.select(this).transition().duration(150).attr("r", 6);

            const tooltip = d3.select("body").append("div")
              .attr("class", "d3-tooltip")
              .style("opacity", 0);

            tooltip.transition().duration(200).style("opacity", .9);
            tooltip.html(`Institución: ${d.institucion}<br/>Año: ${d.anio}<br/>Citas totales: ${d.citas_totales}<br/>Promedio: ${d.citas_promedio}`)
              .style("left", (event.pageX + 10) + "px")
              .style("top", (event.pageY - 28) + "px");
          })
          .on("mouseout", function() {
            d3.select(this).transition().duration(150).attr("r", 4);
            d3.selectAll(".d3-tooltip").remove();
          })
          .transition()
          .duration(600)
          .delay((d, i) => i * 90 + 600 + (index * 120))
          .attr("r", 4);
      });

      g.append("g")
        .attr("transform", `translate(0,${height})`)
        .call(d3.axisBottom(xScale).tickFormat(d3.format("d")))
        .selectAll("text")
        .style("font-family", "Inter, sans-serif")
        .style("font-size", "12px");

      g.append("g")
        .call(d3.axisLeft(yScale))
        .selectAll("text")
        .style("font-family", "Inter, sans-serif")
        .style("font-size", "12px");

      return;
    }

    createTendenciaCitasLegacy();
  };

  const createTiposPublicacion = () => {
    if (!data.tipos_publicacion || data.tipos_publicacion.length === 0) return;

    const container = d3.select(tiposRef.current);
    container.selectAll("*").remove();

    const width = 300;
    const height = 300;
    const radius = Math.min(width, height) / 2 - 20;

    const svg = container
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(data.tipos_publicacion.map(d => d.tipo))
      .range(["#6366F1", "#8B5CF6", "#A855F7", "#D946EF", "#EC4899", "#F43F5E", "#F97316", "#FBBF24"]);

    // Pie generator
    const pie = d3.pie()
      .value(d => d.cantidad)
      .sort(null);

    // Arc generator
    const arc = d3.arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius);

    const arcHover = d3.arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius + 5);

    // Crear arcos
    const arcs = g.selectAll(".arc")
      .data(pie(data.tipos_publicacion))
      .enter().append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.tipo))
      .style("opacity", 0)
      .on("mouseover", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arcHover);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.data.tipo}<br/>Cantidad: ${d.data.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .style("opacity", 1)
      .attrTween("d", function(d) {
        const interpolate = d3.interpolate({startAngle: 0, endAngle: 0}, d);
        return function(t) {
          return arc(interpolate(t));
        };
      });
  };

  const createIdiomasChart = () => {
    if (!data.idiomas || data.idiomas.length === 0) return;

    const container = d3.select(idiomasRef.current);
    container.selectAll("*").remove();

    const margin = { top: 20, right: 30, bottom: 40, left: 80 };
    const width = 350 - margin.left - margin.right;
    const height = 250 - margin.top - margin.bottom;

    const svg = container
      .append("svg")
      .attr("width", width + margin.left + margin.right)
      .attr("height", height + margin.top + margin.bottom);

    const g = svg.append("g").attr("transform", `translate(${margin.left},${margin.top})`);

    // Escalas
    const yScale = d3.scaleBand()
      .domain(data.idiomas.map(d => d.idioma))
      .range([0, height])
      .padding(0.2);

    const maxIdi = d3.max(data.idiomas, d => d.cantidad) ?? 0;
    const xScale = d3.scaleLinear()
      .domain([0, Math.max(maxIdi, 1)])
      .range([0, width]);

    // Color scale
    const colorScale = d3.scaleSequential(d3.interpolateCool)
      .domain([0, Math.max(maxIdi, 1)]);

    // Barras
    g.selectAll(".bar")
      .data(data.idiomas)
      .enter().append("rect")
      .attr("class", "bar")
      .attr("y", d => yScale(d.idioma))
      .attr("height", yScale.bandwidth())
      .attr("x", 0)
      .attr("width", 0)
      .attr("fill", d => colorScale(d.cantidad))
      .attr("rx", 4)
      .on("mouseover", function(event, d) {
        d3.select(this).style("opacity", 0.8);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.idioma}<br/>Publicaciones: ${d.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function() {
        d3.select(this).style("opacity", 1);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .attr("width", d => xScale(d.cantidad));

    // Ejes
    g.append("g")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "12px");

    g.append("g")
      .call(d3.axisLeft(yScale))
      .selectAll("text")
      .style("font-family", "Inter, sans-serif")
      .style("font-size", "11px");
  };

  const createOpenAccessChart = () => {
    if (!data.open_access || data.open_access.length === 0) return;

    const container = d3.select(oaRef.current);
    container.selectAll("*").remove();

    const width = 300;
    const height = 300;
    const radius = Math.min(width, height) / 2 - 20;

    const svg = container
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

    // Color scale - verde para OA, gris para cerrado
    const color = d3.scaleOrdinal()
      .domain(["Open Access", "Acceso Cerrado"])
      .range(["#10B981", "#6B7280"]);

    // Pie generator
    const pie = d3.pie()
      .value(d => d.cantidad)
      .sort(null);

    // Arc generator
    const arc = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius);

    const arcHover = d3.arc()
      .innerRadius(radius * 0.6)
      .outerRadius(radius + 5);

    // Crear arcos
    const arcs = g.selectAll(".arc")
      .data(pie(data.open_access))
      .enter().append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.tipo))
      .style("opacity", 0)
      .on("mouseover", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arcHover);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.data.tipo}<br/>Publicaciones: ${d.data.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .style("opacity", 1)
      .attrTween("d", function(d) {
        const interpolate = d3.interpolate({startAngle: 0, endAngle: 0}, d);
        return function(t) {
          return arc(interpolate(t));
        };
      });

    // Centro con porcentaje OA
    const oaData = data.open_access.find(d => d.tipo === "Open Access");
    const total = data.open_access.reduce((sum, d) => sum + d.cantidad, 0);
    const pctOA = total > 0 ? Math.round((oaData?.cantidad || 0) / total * 100) : 0;

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "-0.2em")
      .style("font-size", "24px")
      .style("font-weight", "bold")
      .style("fill", "#10B981")
      .text(`${pctOA}%`);

    g.append("text")
      .attr("text-anchor", "middle")
      .attr("dy", "1.2em")
      .style("font-size", "12px")
      .style("fill", "#64748B")
      .text("Open Access");
  };

  const createGeneroChart = () => {
    if (!data.genero || data.genero.length === 0) return;

    const container = d3.select(generoRef.current);
    container.selectAll("*").remove();

    const width = 280;
    const height = 280;
    const radius = Math.min(width, height) / 2 - 20;

    const svg = container
      .append("svg")
      .attr("width", width)
      .attr("height", height);

    const g = svg.append("g").attr("transform", `translate(${width / 2},${height / 2})`);

    // Color scale
    const color = d3.scaleOrdinal()
      .domain(["Masculino", "Femenino", "Otro", "No especificado"])
      .range(["#3B82F6", "#EC4899", "#8B5CF6", "#94A3B8"]);

    // Pie generator
    const pie = d3.pie()
      .value(d => d.cantidad)
      .sort(null);

    // Arc generator
    const arc = d3.arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius);

    const arcHover = d3.arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius + 5);

    // Crear arcos
    const arcs = g.selectAll(".arc")
      .data(pie(data.genero))
      .enter().append("g")
      .attr("class", "arc");

    arcs.append("path")
      .attr("d", arc)
      .attr("fill", d => color(d.data.genero))
      .style("opacity", 0)
      .on("mouseover", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arcHover);
        
        const tooltip = d3.select("body").append("div")
          .attr("class", "d3-tooltip")
          .style("opacity", 0);
        
        tooltip.transition().duration(200).style("opacity", .9);
        tooltip.html(`${d.data.genero}<br/>Investigadores: ${d.data.cantidad}`)
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr("d", arc);
        d3.selectAll(".d3-tooltip").remove();
      })
      .transition()
      .duration(1000)
      .delay((d, i) => i * 100)
      .style("opacity", 1)
      .attrTween("d", function(d) {
        const interpolate = d3.interpolate({startAngle: 0, endAngle: 0}, d);
        return function(t) {
          return arc(interpolate(t));
        };
      });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <span className="material-icons-round text-primary text-6xl animate-spin">refresh</span>
          <p className="text-slate-500 dark:text-slate-400 mt-4">Cargando visualizaciones...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <span className="material-icons-round text-red-400 text-6xl">error</span>
          <p className="text-red-500 mt-4">Error al cargar los datos del dashboard</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Filtros */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl p-6 border border-border-light dark:border-border-dark shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Institución
            </label>
            <select
              value={filters.institucion}
              onChange={(e) => setFilters(prev => ({...prev, institucion: e.target.value}))}
              className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Todas las instituciones</option>
              {data.filtros?.instituciones?.map(inst => (
                <option key={inst} value={inst}>{inst}</option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Área de Conocimiento
            </label>
            <select
              value={filters.area}
              onChange={(e) => setFilters(prev => ({...prev, area: e.target.value}))}
              className="block w-full px-3 py-2 border border-border-light dark:border-border-dark rounded-md shadow-sm bg-slate-50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="all">Todas las áreas</option>
              {data.filtros?.areas?.map(area => (
                <option key={area} value={area}>{area}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* KPIs - Primera fila */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">article</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.total_publicaciones || 0}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Publicaciones</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-secondary to-indigo-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">people</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.total_investigadores || 0}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Investigadores</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">format_quote</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{(data.kpis?.total_citas || 0).toLocaleString()}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Citas Totales</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">lock_open</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.pct_open_access || 0}%</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Open Access</div>
            </div>
          </div>
        </div>
      </div>

      {/* KPIs - Segunda fila */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">auto_awesome</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.pct_open_access || 0}%</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Open Access</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">analytics</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.promedio_citas || 0}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Citas/Pub</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">library_books</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.total_journals || 0}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Journals</div>
            </div>
          </div>
        </div>
        <div className="glass-panel rounded-xl p-5 border border-white/20 shadow-lg dark:border-slate-700 relative overflow-hidden group hover:scale-[1.02] transition-all duration-300">
          <div className="flex items-center">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mr-3 shadow-lg">
              <span className="material-icons-round text-white text-lg">verified</span>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-900 dark:text-white">{data.kpis?.total_open_access || 0}</div>
              <div className="text-xs font-medium text-slate-600 dark:text-slate-400">Pubs OA</div>
            </div>
          </div>
        </div>
      </div>

      {/* Graficos principales - Primera fila */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-primary">bar_chart</span>
            Publicaciones por Año
          </h3>
          <div ref={pubAnioRef} className="d3-chart-container"></div>
        </div>
        
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-secondary">emoji_events</span>
            Top 10 Investigadores
          </h3>
          <div ref={topInvRef} className="d3-chart-container"></div>
        </div>
      </div>

      {/* Graficos - Segunda fila */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-emerald-500">account_balance</span>
            Distribucion por Institución
          </h3>
          <div ref={instRef} className="d3-chart-container flex justify-center"></div>
        </div>

        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-amber-500">apartment</span>
            Departamentos (Investigadores)
          </h3>
          <div ref={deptRef} className="d3-chart-container"></div>
        </div>
      </div>

      {/* Graficos - Tercera fila */}
      <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <span className="material-icons-round mr-2 text-amber-500">science</span>
          Áreas de Conocimiento
        </h3>
        <div ref={areasRef} className="d3-chart-container"></div>
      </div>

      {/* Grafico de tendencia de citas */}
      <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
          <span className="material-icons-round mr-2 text-emerald-500">trending_up</span>
          Tendencia de Citas por Año
        </h3>
        <div ref={tendenciaRef} className="d3-chart-container flex justify-center"></div>
      </div>

      {/* Nuevos graficos - Tipos, Idiomas, OA, Genero */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-pink-500">wc</span>
            Genero
          </h3>
          <div ref={generoRef} className="d3-chart-container flex justify-center"></div>
        </div>
      
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-cyan-500">translate</span>
            Idiomas
          </h3>
          <div ref={idiomasRef} className="d3-chart-container"></div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-green-500">lock_open</span>
            Open Access
          </h3>
          <div ref={oaRef} className="d3-chart-container flex justify-center"></div>
        </div>
        
        <div className="glass-panel rounded-xl p-6 border border-white/20 shadow-lg dark:border-slate-700">
          <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4 flex items-center">
            <span className="material-icons-round mr-2 text-purple-500">category</span>
            Tipos de Publicación
          </h3>
          <div ref={tiposRef} className="d3-chart-container flex justify-center"></div>
        </div>
      </div>
    </div>
  );
}

export default D3Dashboard;
